import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Approvers } from '../../shared/approvers/approvers';
import { VIPVendorList } from '../../shared/vendor-vip-list/vendor-vip-list';
import { VendorForm } from './vendor-form/vendor-form';
import { Approver, VendorInfo } from '../../shared/type';
import { Button, Checkbox, StatusIconProtoProps } from '@nike/eds';
import { useI18nContext } from 'context/language/use-i18n-context';
import { ts } from './i18n';
import { usePassPolicies } from 'context/PassPolicy';
import { GuestPass, PassRequest } from 'models/IPassRequest';
import { registerPass } from 'services/pass-service';
import { useUserContext } from '../../../context/user';
import { HttpStatusCode } from 'axios';
import { toUTCDate } from '../../../utils/date-helper';
import { AppConfig } from '../../../utils/constants';
import { VIPVendorInfo } from '../../shared/vendor-vip-info/vendor-vip-info';
import { VendorPass } from './type';
import { VisitorPass } from 'models/IVisitorPass';
import dayjs from 'dayjs';
import { VendorBulkUpload } from './bulk-upload/bulk-upload';
import styles from './vendor-pass.module.css';
import { useSponsorContext } from 'context/Sponsor';

const vendorInitValues = { email: '', firstName: '', lastName: '', weChatId: null };
const vendorFormValues: VendorPass = {
  companyName: '',
  shoppingDateStart: '',
  shoppingDateEnd: '',
  passLocationId: 0,
};

const vendorPassDefault: GuestPass = {
  passId: null,
  sponsorId: 0,
  passTypeId: 20,
  firstName: '',
  lastName: '',
  emailAddress: '',
  shoppingDateStart: new Date(),
  shoppingDateEnd: new Date(),
  companyName: '',
  compAmount: '',
  compNumber: '',
  passLocationId: 0,
  passPolicyId: 0,
  relationshipId: null,
  weChatId: null,
  timeSlotId: null,
  govtComplianceChecked: false,
  emailLanguageGroupId: null,
  guestEmailLanguage: '',
};

const isSelectedVendorEmpty = (vendor: VendorInfo) => {
  return vendor.email === '' && vendor.firstName === '' && vendor.lastName === '';
};

type Props = {
  onDismiss: () => void;
  setSnackBar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
  vendorPassToEdit: VisitorPass | null;
  isBulkUpload?: boolean;
  handleBulkUpload: () => void;
};

export const Vendor: FC<Props> = ({
  onDismiss,
  setSnackBar,
  vendorPassToEdit,
  isBulkUpload,
  handleBulkUpload,
}) => {
  const { i18nString } = useI18nContext();
  const passPoliciesContext = usePassPolicies();
  const { passplayUser } = useUserContext();

  const [vendorList, setVendorList] = useState<VendorInfo[]>();
  const [vendorPass, setVendorPass] = useState<VendorPass>();
  const [approver, setApprover] = useState<Approver>();
  const [selectedVendor, setSelectedVendor] = useState<VendorInfo>(vendorInitValues);
  const { sponsor } = useSponsorContext();
  const vendorPolicy = passPoliciesContext.passPolicies.find(
    policy => policy.passTypeToken === 'PASS_TYPE_VENDOR',
  );

  useEffect(() => {
    if (vendorPolicy && (vendorPassToEdit?.passId ?? 0 > 0)) {
      const editVendorForm: VendorPass = {
        companyName: vendorPassToEdit?.companyName ?? '',
        shoppingDateStart: dayjs(vendorPassToEdit?.shoppingDateStart).format('YYYY-MM-DD') ?? '',
        shoppingDateEnd: dayjs(vendorPassToEdit?.shoppingDateEnd).format('YYYY-MM-DD') ?? '',
        passLocationId: vendorPassToEdit?.passLocationId ?? 0,
      };
      setVendorPass(editVendorForm);
      const editVendorInfo: VendorInfo = {
        email: vendorPassToEdit?.emailAddress ?? '',
        firstName: vendorPassToEdit?.firstName ?? '',
        lastName: vendorPassToEdit?.lastName ?? '',
        weChatId: vendorPassToEdit?.weChatId ?? null,
      };
      setSelectedVendor(editVendorInfo);
    } else if (vendorPolicy) {
      setVendorPass(vendorFormValues);
      setSelectedVendor(vendorInitValues);
    }
  }, [vendorPolicy]);

  const handleApproverChange = (option: any) => {
    setApprover({ id: option.value, name: option.label });
  };

  const handleVendorAdd = (vendorInfo: VendorInfo) => {
    setVendorList(prev => {
      if (prev) {
        return [...prev, vendorInfo];
      } else {
        return [vendorInfo];
      }
    });
    setSelectedVendor(vendorInitValues);
  };

  const handleVIPDelete = (vendor: VendorInfo) => {
    setVendorList(prev =>
      prev?.filter(
        v => v.firstName + v.lastName + v.email !== vendor.firstName + v.lastName + vendor.email,
      ),
    );
  };

  const handleVendorEdit = (vendor: VendorInfo) => {
    if (isSelectedVendorEmpty(selectedVendor)) {
      setVendorList(prev =>
        prev?.filter(
          v => v.firstName + v.lastName + v.email !== vendor.firstName + v.lastName + vendor.email,
        ),
      );
      setSelectedVendor(vendor);
    }
  };
  const handleInfoEdit = (vendorInfo: VendorInfo) => {
    if (
      (vendorInfo.email === '' && vendorPolicy?.emailFlag == AppConfig.emailFlags.required) ||
      vendorInfo.firstName === '' ||
      vendorInfo.lastName === ''
    ) {
      setVendorList([]);
    } else {
      setVendorList([vendorInfo]);
    }
    setSelectedVendor(vendorInfo);
  };

  const handleRegister = async () => {
    if (vendorPolicy === undefined) return;
    const vendorPassRequest: PassRequest = {
      approver: passplayUser?.selfApprover ? passplayUser?.selfApprovalId : (approver?.id ?? ''),
      guestPasses:
        vendorList?.map(
          vendor =>
            ({
              ...vendorPassDefault,
              passId: vendorPassToEdit?.passId ?? null,
              firstName: vendor.firstName,
              sponsorId: passplayUser?.sponsorId ?? 0,
              lastName: vendor.lastName,
              emailAddress: vendor.email,
              companyName: vendorPass?.companyName,
              passPolicyId: vendorPolicy.passPolicyId,
              shoppingDateStart: toUTCDate(new Date(vendorPass?.shoppingDateStart ?? '')),
              shoppingDateEnd: toUTCDate(new Date(vendorPass?.shoppingDateEnd ?? '')),
              passLocationId: vendorPass?.passLocationId ?? 0,
              weChatId: vendor.weChatId,
            }) as GuestPass,
        ) ?? ([] as GuestPass[]),
    };
    try {
      const response = await registerPass(passplayUser?.sponsorId, vendorPassRequest);
      if (response.status === HttpStatusCode.Ok) {
        setVendorList([]);
        setSelectedVendor(vendorInitValues);
        let message = ts.TOASTR_PASS_ADDED;
        if (vendorPassToEdit && vendorPassToEdit?.passId > 0) {
          message = ts.TOASTR_CHANGES_SAVED;
        }
        setSnackBar({
          showSnack: true,
          message: i18nString(message).replace(/\{{\w+\}}/g, 'Vendor'),
          snackStatus: 'success',
        });
        onDismiss();
      } else {
        setSnackBar({
          showSnack: true,
          message: i18nString(ts.TOASTR_PASS_ERROR),
          snackStatus: 'error',
        });
      }
    } catch (e) {
      setSnackBar({
        showSnack: true,
        message: i18nString(ts.TOASTR_PASS_ERROR),
        snackStatus: 'error',
      });
      onDismiss();
    }
  };

  const disableRegister = (): boolean | undefined => {
    return (
      vendorList === undefined ||
      vendorList?.length === 0 ||
      (approver == undefined && !passplayUser?.selfApprover && vendorPolicy?.approvalFlag === 1) ||
      (vendorPolicy?.passesAllowed == 0
        ? false
        : (vendorPolicy?.passesAllowed || 0) - (vendorPolicy?.passesUsed || 0) == 0) ||
      vendorPass?.companyName == '' ||
      vendorPass?.shoppingDateStart == '' ||
      vendorPass?.passLocationId == 0 ||
      (vendorPolicy?.policyLocationId === 156 && !govtCompliance)
    );
  };
  const handleBulkList = (vendorList: VendorInfo[]) => {
    setVendorList(vendorList);
    setSelectedVendor(vendorInitValues);
    handleBulkUpload();
  };
  const [govtCompliance, setGovtCompliance] = useState<boolean>(false);
  const handleGovtCompliance = () => {
    setGovtCompliance(!govtCompliance);
  };
  return (
    <div data-testid="vendor-pass" className="pass_modal_width">
      {!passplayUser?.selfApprover && vendorPolicy?.approvalFlag === 1 && (
        <Approvers onApproverChange={handleApproverChange} />
      )}
      {vendorPolicy && vendorPass && (
        <section className={!passplayUser?.selfApprover ? 'eds-spacing--mt-16' : ''}>
          <VendorForm
            vendorFormValues={vendorPass}
            passPolicy={vendorPolicy}
            handleFormChanges={setVendorPass}
          />
        </section>
      )}
      {!isBulkUpload && (
        <section
          className={`eds-flex eds-flex--direction-column eds-flex--justify-content-center eds-spacing--mt-16 eds-background--white section-border`}
        >
          <VIPVendorInfo
            onVIPVendorAdd={handleVendorAdd}
            onVIPVendorClear={() => setSelectedVendor(vendorInitValues)}
            vipVendorToEdit={selectedVendor}
            emailRequired={vendorPolicy?.emailFlag}
            passType={vendorPolicy?.passTypeId ?? 20}
            isEdit={vendorPassToEdit !== null}
            handleEdit={handleInfoEdit}
            count={vendorList?.length ?? 0}
          />
          {vendorPassToEdit === null && (
            <VIPVendorList
              showEmail={vendorPolicy?.emailFlag != 0}
              VIPVendorList={vendorList}
              handleDelete={handleVIPDelete}
              handleEdit={handleVendorEdit}
              passType={vendorPolicy?.passTypeId ?? 20}
            />
          )}
        </section>
      )}

      {passplayUser?.canDoBulkUpload && isBulkUpload && (
        <section
          className={`eds-flex eds-flex--direction-column eds-flex--justify-content-center eds-spacing--mt-16 eds-background--white section-border`}
        >
          {vendorPolicy && (
            <VendorBulkUpload
              passPolicy={vendorPolicy}
              onVendorAdd={handleBulkList}
              setSnackBar={setSnackBar}
            />
          )}
        </section>
      )}
      {vendorPolicy?.policyLocationId === 156 && (
        <section className={`eds-flex eds-background--white eds-spacing--mt-16 section-border`}>
          <div className={styles.modal__panel}>
            <div>
              <Checkbox
                className={'eds-spacing--mr-12 eds-spacing--mt-6 checkbox_compl'}
                id="govtCompliance"
                name="govtCompliance"
                hideLabel={true}
                label="Government Complinace"
                aria-label="Government Complinace Checkbox"
                onClick={() => handleGovtCompliance()}
              />
              {sponsor.culture !== 'zh-CN' && (
                <>
                  <span className={styles.txt_compliance}>
                    *I acknowledge and confirm that the recipient and user of the pass requested is
                    neither a government official nor a person working in a government organization
                    or a state-owned entity. I acknowledge having read and understood the
                    <a
                      target="_blank"
                      rel="noreferrer"
                      className={styles.greenText}
                      href="https://nike.sharepoint.com/teams/ap5/NikeGCLegal/PublishingImages/Lists/PoliciesList/EditForm/Mainland%20China%20Rider%20on%20Government%20Officials%20Gifts%20and%20Hospitality%20-%20EN.PDF"
                    >
                      {' '}
                      Mainland China Rider to &quot;Government Official&quot; Gifts and Hospitality
                      Policy.
                    </a>
                  </span>
                </>
              )}
              {sponsor.culture === 'zh-CN' && (
                <>
                  <span className={styles.txt_compliance}>
                    *我确认我已阅读并理解公司《
                    <a
                      target="_blank"
                      rel="noreferrer"
                      className={styles.greenText}
                      href="https://nike.sharepoint.com/teams/ap5/NikeGCLegal/PublishingImages/Lists/PoliciesList/EditForm/Mainland%20China%20Rider%20on%20Government%20Officials%20Gifts%20and%20Hospitality%20-%20CN.PDF"
                    >
                      中国大陆&quot;政府官员&quot;礼品及费用政策
                    </a>
                    》。 我确认本券的接收者及使用者不是政府官员或在政府及国有企业内任职的员工。
                  </span>
                </>
              )}

              <div className="cb"></div>
            </div>
          </div>
        </section>
      )}
      <div className="eds-flex eds-flex--justify-content-center eds-spacing--mt-48">
        {!isBulkUpload && (
          <Button
            className="txt_uppercase"
            data-testid="register"
            disabled={disableRegister()}
            size="medium"
            variant="primary"
            type="submit"
            onClick={() => handleRegister()}
          >
            {vendorPassToEdit?.passId && vendorPassToEdit.passId > 0
              ? i18nString(ts.PASS_MODAL_UPDATE)
              : i18nString(ts.PASS_MODAL_REGISTER)}
          </Button>
        )}
        {isBulkUpload && (
          <Button
            className="txt_uppercase"
            data-testid="cancel"
            size="medium"
            variant="primary"
            onClick={handleBulkUpload}
          >
            {i18nString(ts.BTN_CANCEL)}
          </Button>
        )}
      </div>
    </div>
  );
};
